import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';
import MemberTimeline from '../t2m/MemberTimeline';
import ContactLines from '../../components/ContactLines';

// ─── Constants ──────────────────────────────────────────────────────────────

const SALE_TYPES = [
  { value: 'new_monthly',      label: 'New — Monthly',      rate: '22%' },
  { value: 'new_annual',       label: 'New — Annual',       rate: '14%' },
  { value: 'winback_monthly',  label: 'Winback — Monthly',  rate: '22%' },
  { value: 'winback_annual',   label: 'Winback — Annual',   rate: '14%' },
  // Save retired as a selectable option (BK decision, Aug 2026) — no flat
  // "Save" tier under current policy; Save Attempted pays the Winback rate.
  // typeBadge() below still recognizes 'save' for any historical rows.
  { value: 'upgrade_monthly',  label: 'Upgrade — Monthly',  rate: 'delta × 22%' },
  { value: 'upgrade_annual',   label: 'Upgrade — Annual',   rate: 'delta × 14%' },
  // Welld (9/8/26) — logged at $0, held until activity_bookings shows 8 real
  // visits since this sale's date, per Commission Standards v3 §11. Sale
  // Price should be entered as 0 — there's no member-paid price to log.
  { value: 'welld',            label: 'Welld / Third-Party Health Plan', rate: 'held — $17.60 at 8 visits' },
];

function typeBadge(saleType) {
  if (saleType?.startsWith('new'))      return <span className="badge badge-blue">New</span>;
  if (saleType?.startsWith('winback'))  return <span className="badge badge-orange">Winback</span>;
  if (saleType?.startsWith('save'))     return <span className="badge badge-gray">Save</span>;
  if (saleType?.startsWith('upgrade'))  return <span className="badge badge-purple">Upgrade</span>;
  if (saleType === 'welld')             return <span className="badge badge-gray" style={{ background: 'rgba(20,184,166,0.15)', color: '#14b8a6' }}>Welld</span>;
  return <span className="badge badge-gray">—</span>;
}

function billingTag(saleType) {
  return saleType?.endsWith('annual') ? 'Annual' : 'Monthly';
}

function fmtMoney(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function RetentionDots({ retention }) {
  if (!retention || retention.length === 0) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const shown = retention.filter(r => [30, 60, 90].includes(r.checkpoint_days));
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {shown.map(r => {
        const color = r.status === 'active' ? 'var(--green)'
          : r.status === 'pending' ? 'var(--yellow)'
          : 'var(--red)';
        return (
          <span key={r.checkpoint_days} title={`${r.checkpoint_days}d — ${r.status}`}
            style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
        );
      })}
    </span>
  );
}

// ─── Welld Ready to Pay panel (9/8/26) ─────────────────────────────────────
// Commission Standards v3 §11: pays once, held at $0 until real visits since
// enrollment (this sale's sale_date) reach 8. Counted from activity_bookings,
// not visits/checkins_ — see getWelldReadyToPay for why. "Mark Paid" reuses
// the existing edit endpoint (salePrice: 80 recalculates to $17.60 via the
// same formula every other sale already uses) rather than a new action.

function WelldReadyPanel({ isAdmin }) {
  const { get, put } = useApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  const load = useCallback(() => {
    get('/commission/welld-ready').then(d => setRows(d || [])).catch(console.error).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function markPaid(saleId) {
    setPaying(saleId);
    try {
      await put(`/commission/sales/${saleId}`, { salePrice: 80 });
      load();
    } catch (e) {
      alert(e.message || 'Failed to mark paid.');
    } finally {
      setPaying(null);
    }
  }

  if (loading || rows.length === 0) return null;

  return (
    <div style={{ ...styles.tableWrapper, marginBottom: '1rem', padding: '0.75rem 0.85rem' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
        🩺 Welld — Pending 8-Visit Threshold ({rows.length})
      </div>
      {rows.map(r => (
        <div key={r.saleId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.83rem' }}>
          <div>
            <strong>{r.name}</strong> <span style={{ color: 'var(--text-secondary)' }}>· {r.email} · {r.locationId} · enrolled {r.saleDate}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: r.readyToPay ? 'var(--green)' : 'var(--text-secondary)', fontWeight: r.readyToPay ? 700 : 400 }}>
              {r.visitsSinceEnrollment}/8 visits
            </span>
            {r.readyToPay && isAdmin && (
              <button className="btn btn-primary btn-sm" disabled={paying === r.saleId} onClick={() => markPaid(r.saleId)}>
                {paying === r.saleId ? 'Paying...' : `Mark Paid (${fmtMoney(r.payAmount)})`}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── KPI bar ────────────────────────────────────────────────────────────────

function KPIBar({ overview }) {
  if (!overview) return null;
  return (
    <div style={styles.kpiBar}>
      <div style={styles.kpiItem}>
        <div style={styles.kpiValue}>{overview.total_sales ?? 0}</div>
        <div style={styles.kpiLabel}>Total Sales</div>
      </div>
      <div style={styles.kpiItem}>
        <div style={{ ...styles.kpiValue, color: 'var(--green)' }}>{fmtMoney(overview.commission_earned)}</div>
        <div style={styles.kpiLabel}>Commission Earned</div>
      </div>
      <div style={styles.kpiItem}>
        <div style={styles.kpiValue}>{overview.new_count ?? 0}</div>
        <div style={styles.kpiLabel}>New</div>
      </div>
      <div style={styles.kpiItem}>
        <div style={{ ...styles.kpiValue, color: 'var(--orange)' }}>{overview.winback_count ?? 0}</div>
        <div style={styles.kpiLabel}>Winback</div>
      </div>
      <div style={styles.kpiItem}>
        <div style={styles.kpiValue}>{overview.save_count ?? 0}</div>
        <div style={styles.kpiLabel}>Save</div>
      </div>
      <div style={styles.kpiItem}>
        <div style={styles.kpiValue}>{overview.annual_count ?? 0}</div>
        <div style={styles.kpiLabel}>Annual Sales</div>
      </div>
      {overview.duplicate_flagged_count > 0 && (
        <div style={{ ...styles.kpiItem, background: 'rgba(239,68,68,0.08)' }}>
          <div style={{ ...styles.kpiValue, color: 'var(--red)' }}>{overview.duplicate_flagged_count}</div>
          <div style={styles.kpiLabel}>Flagged Dupes</div>
        </div>
      )}
    </div>
  );
}

// ─── Log Sale modal ─────────────────────────────────────────────────────────

function LogSaleModal({ onClose, onSaved }) {
  const { get, post } = useApi();
  const { currentClub, availableClubs } = useClub();

  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    memberEmail: '', memberFirstName: '', memberLastName: '',
    locationId: currentClub?.id || '',
    saleType: '', membershipPlan: '', salePrice: '', billingCycle: 'monthly',
    saleDate: today, notes: '', oldPrice: '', rackPrice: '',
  });
  const [classification, setClassification] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [dupeWarning, setDupeWarning] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hasSearchedEmail, setHasSearchedEmail] = useState(false);
  const emailDebounceRef = useRef(null);

  function update(f, v) { setForm(x => ({ ...x, [f]: v })); setDupeWarning(null); setIsDirty(true); }

  // Autocomplete against real COMS member records — same /member/search
  // endpoint Member Search uses. This eliminates the exact typo pattern that
  // caused all 4 unverifiable July sales (Josh retyping an email from memory)
  // for Winback/Upgrade, since that member already exists in the DB and can
  // be selected rather than typed. It does NOT help New sales — a brand-new
  // signup hasn't been ingested by the pipeline yet, so there's nothing to
  // match against; typing a fresh email correctly is still on the rep for
  // that case. Finding zero suggestions is itself a useful signal ("this
  // really is a new person"), not a failure of the feature.
  function handleEmailChange(v) {
    update('memberEmail', v);
    clearTimeout(emailDebounceRef.current);
    setHasSearchedEmail(false);
    if (v.trim().length < 3) { setEmailSuggestions([]); setShowSuggestions(false); return; }
    emailDebounceRef.current = setTimeout(() => {
      get(`/member/search?q=${encodeURIComponent(v.trim())}`)
        .then(d => { setEmailSuggestions(d.members || []); setShowSuggestions(true); setHasSearchedEmail(true); })
        .catch(() => { setEmailSuggestions([]); setHasSearchedEmail(true); });
    }, 350);
  }

  function selectSuggestion(m) {
    const nameParts = (m.full_name || '').trim().split(' ');
    const email = m.email;
    const locationId = m.location_id || form.locationId;
    setForm(x => ({
      ...x,
      memberEmail: email,
      memberFirstName: m.first_name || nameParts[0] || '',
      // FIX (9/8/26): was `m.last_initial || nameParts.slice(1).join(' ')` —
      // prioritized the truncated last_initial field over the real full last
      // name, so every autocomplete selection silently saved "Abhishek T"
      // instead of "Abhishek Tyagi". Full name now wins whenever it's
      // available; last_initial is only a fallback if full_name has nothing
      // past the first word. This is a pure reorder of data already present
      // client-side — doesn't depend on the search API returning anything new.
      memberLastName: nameParts.slice(1).join(' ') || m.last_initial || '',
      locationId,
    }));
    setShowSuggestions(false);
    setEmailSuggestions([]);
    setIsDirty(true);
    // Explicit call rather than relying on onBlur — selecting via mousedown
    // races against the blur event and the memoized checkStatus() can still
    // hold the pre-selection email at that instant.
    setClassifying(true);
    get(`/commission/classify?email=${encodeURIComponent(email)}&club=${locationId}`)
      .then(setClassification)
      .catch(() => setClassification(null))
      .finally(() => setClassifying(false));
  }

  // Accidental backdrop clicks were silently discarding a half-filled form
  // with no way back — now only closes without confirmation if nothing's
  // been typed yet.
  function handleBackdropClick() {
    if (!isDirty || window.confirm('Discard this sale — you have unsaved changes?')) {
      onClose();
    }
  }

  const checkStatus = useCallback(() => {
    if (!form.memberEmail || !form.locationId) return;
    setClassifying(true);
    get(`/commission/classify?email=${encodeURIComponent(form.memberEmail)}&club=${form.locationId}`)
      .then(setClassification)
      .catch(() => setClassification(null))
      .finally(() => setClassifying(false));
  }, [form.memberEmail, form.locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave(overrideDedup = false) {
    setError(null);
    if (!form.memberEmail || !form.memberFirstName || !form.memberLastName || !form.saleType ||
        !form.membershipPlan || !form.salePrice || !form.saleDate || !form.rackPrice) {
      setError('All fields except Notes are required.');
      return;
    }
    const isUpgrade = form.saleType.startsWith('upgrade');
    if (isUpgrade && !form.oldPrice) {
      setError('Old Price is required for Upgrade sales — commission is calculated on the price difference, not the full price.');
      return;
    }
    setSaving(true);
    try {
      const result = await post('/commission/sales', {
        ...form,
        salePrice: Number(form.salePrice),
        oldPrice: isUpgrade ? Number(form.oldPrice) : undefined,
        rackPrice: form.rackPrice ? Number(form.rackPrice) : undefined,
        overrideDedup,
      });
      if (result.duplicate) {
        setDupeWarning(result.existingSales);
        setSaving(false);
        return;
      }
      onSaved();
    } catch (e) {
      setError(e.message || 'Failed to log sale.');
    } finally {
      setSaving(false);
    }
  }

  const isUpgrade = form.saleType.startsWith('upgrade');
  const suggested = classification?.suggestedSaleType;
  const suggestedLabel = classification?.lapseStatus === 'never_member' ? 'Never a member → New'
    : classification?.lapseStatus === 'active' ? 'Currently active & paying → Upgrade (delta commission — enter Old Price below)'
    : classification?.lapseStatus === 'active_club_process' ? '⚠️ Club has an open Payment/Cancellation case on this member that hasn\'t escalated yet — not yet eligible for a Josh sale. Confirm with the club before logging.'
    : classification?.lapseStatus === 'lapsed_no_tracked_process_post_rollout' ? `⚠️ Lapsed ${classification.daysSinceLapse ?? '?'} days (on/after Aug 1) with NO tracked club process — this may be a detection gap, not a real "no process" case. Double-check with the club before logging as New.`
    : classification?.lapseStatus === 'lapsed_no_tracked_process' ? `Lapsed ${classification.daysSinceLapse ?? '?'} days, before tracking began — no club process on file → New (expected, not a gap)`
    : classification?.lapseStatus === 'lapsed' ? `Lapsed ${classification.daysSinceLapse ?? '?'} days, club escalated${classification.winbackSubType ? ` (${classification.winbackSubType === 'save_attempted' ? 'Save Attempted' : 'Uncontacted'})` : ''} → Winback`
    : null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="modal-title">Log Commission Sale</div>

        {error && (
          <div style={{ padding: '0.6rem 0.75rem', background: 'rgba(255,50,50,0.1)', border: '1px solid var(--red)', borderRadius: 6, fontSize: '0.83rem', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {dupeWarning && (
          <div style={{ padding: '0.6rem 0.75rem', background: 'rgba(234,179,8,0.1)', border: '1px solid var(--yellow)', borderRadius: 6, fontSize: '0.8rem', marginBottom: '1rem' }}>
            ⚠️ This email already has a sale logged this month ({dupeWarning[0]?.sale_type}, {fmtMoney(dupeWarning[0]?.commission_amount)}).
            <div style={{ marginTop: 8 }}>
              <button className="btn btn-danger btn-sm" onClick={() => handleSave(true)}>Log anyway (acknowledged duplicate)</button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div className="field-group" style={{ gridColumn: 'span 2', position: 'relative' }}>
            <label className="field-label">Member Email *</label>
            <input className="field-input" value={form.memberEmail}
              onChange={e => handleEmailChange(e.target.value)}
              onFocus={() => emailSuggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => { checkStatus(); setTimeout(() => setShowSuggestions(false), 150); }}
              placeholder="name@email.com — start typing to search existing members" autoComplete="off" />
            {showSuggestions && emailSuggestions.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6,
                marginTop: 2, maxHeight: 220, overflowY: 'auto',
              }}>
                {emailSuggestions.slice(0, 8).map(m => (
                  <div key={m.member_id + m.location_id}
                    onMouseDown={() => selectSuggestion(m)}
                    style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem', borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: 700, color: 'var(--text)' }}>{m.full_name} <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>· {m.location_id}</span></div>
                    <div style={{ color: 'var(--text-secondary)' }}>{m.email}{m.membership_type ? ` · ${m.membership_type}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
            {hasSearchedEmail && emailSuggestions.length === 0 && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3 }}>
                No existing member found — fine for a New sale, double-check spelling if this should be a Winback/Upgrade.
              </div>
            )}
          </div>
          <div className="field-group">
            <label className="field-label">First Name *</label>
            <input className="field-input" value={form.memberFirstName} onChange={e => update('memberFirstName', e.target.value)} />
          </div>
          <div className="field-group">
            <label className="field-label">Last Name *</label>
            <input className="field-input" value={form.memberLastName} onChange={e => update('memberLastName', e.target.value)} />
            {/* Added 9/8/26 — visible confirmation of which real email this
                record will save against, right where the name is being
                double-checked. Names get typo'd, shortened, or mismatched to
                the wrong person (multiple real cases found in a full August
                commission audit); the email is the one unambiguous identifier. */}
            {form.memberEmail && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3 }}>
                Saving against: <span style={{ color: 'var(--text)' }}>{form.memberEmail}</span>
              </div>
            )}
          </div>
          <div className="field-group">
            <label className="field-label">Club *</label>
            <select className="field-select" value={form.locationId}
              onChange={e => { update('locationId', e.target.value); }}>
              {availableClubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label className="field-label">Sale Date *</label>
            <input type="date" className="field-input" value={form.saleDate} onChange={e => update('saleDate', e.target.value)} />
          </div>
        </div>

        {/* Live classification suggestion — Sec 7.2 */}
        {form.memberEmail && (
          <div style={{
            padding: '0.6rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.8rem',
            background: ['active_club_process', 'lapsed_no_tracked_process_post_rollout'].includes(classification?.lapseStatus) ? 'rgba(239,68,68,0.1)'
              : 'var(--surface-2)',
            border: `1px solid ${['active_club_process', 'lapsed_no_tracked_process_post_rollout'].includes(classification?.lapseStatus) ? 'var(--red)'
              : 'var(--border)'}`,
          }}>
            {classifying ? 'Checking member status…' : suggestedLabel
              ? <>🔎 <strong>{suggestedLabel}</strong>{classification?.lapseStatus !== 'active_club_process' ? ' — confirm the Sale Type below matches, or override if you know better.' : ''}</>
              : <span style={{ color: 'var(--text-secondary)' }}>Click out of the email field to check member status.</span>}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div className="field-group" style={{ gridColumn: 'span 2' }}>
            <label className="field-label">Sale Type *</label>
            <select className="field-select" value={form.saleType} onChange={e => update('saleType', e.target.value)}>
              <option value="">Select...</option>
              {SALE_TYPES.map(t => (
                <option key={t.value} value={t.value}>
                  {t.label} ({t.rate}){suggested && t.value.startsWith(suggested) ? ' — suggested' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="field-group" style={{ gridColumn: 'span 2' }}>
            <label className="field-label">Membership Plan *</label>
            <input className="field-input" value={form.membershipPlan} onChange={e => update('membershipPlan', e.target.value)}
              placeholder="e.g. Nationwide Unlimited" />
          </div>
          {isUpgrade && (
            <div className="field-group" style={{ gridColumn: 'span 2' }}>
              <label className="field-label">Old Price (prior plan) *</label>
              <input type="number" step="0.01" className="field-input" value={form.oldPrice} onChange={e => update('oldPrice', e.target.value)}
                placeholder="What they were already paying" />
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                Commission is calculated on the difference (New Price − Old Price), not the full new price.
              </div>
            </div>
          )}
          <div className="field-group">
            <label className="field-label">Price *</label>
            <input type="number" step="0.01" className="field-input" value={form.salePrice} onChange={e => update('salePrice', e.target.value)} />
          </div>
          <div className="field-group">
            <label className="field-label">Billing Cycle *</label>
            <select className="field-select" value={form.billingCycle} onChange={e => update('billingCycle', e.target.value)}>
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div className="field-group" style={{ gridColumn: 'span 2' }}>
            <label className="field-label">Rack Price (list/MSRP) *</label>
            <input type="number" step="0.01" className="field-input" value={form.rackPrice} onChange={e => update('rackPrice', e.target.value)}
              placeholder="What this plan lists for, before any discount" />
            {form.rackPrice && form.salePrice && (
              <div style={{ fontSize: '0.72rem', marginTop: 4, color: Number(form.rackPrice) > Number(form.salePrice) ? 'var(--yellow)' : 'var(--text-secondary)' }}>
                {Number(form.rackPrice) > Number(form.salePrice)
                  ? `Sold $${(Number(form.rackPrice) - Number(form.salePrice)).toFixed(2)} under rack — tracked for trend, not a problem on its own.`
                  : 'At or above rack price.'}
              </div>
            )}
          </div>
        </div>

        <div className="field-group" style={{ marginBottom: '0.75rem' }}>
          <label className="field-label">Notes</label>
          <textarea className="field-textarea" rows={2} value={form.notes} onChange={e => update('notes', e.target.value)}
            placeholder="e.g. has 1 month free Wingfield" />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={() => handleSave(false)}>
            {saving ? 'Saving...' : 'Log Sale'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Sale modal (9/8/26) ──────────────────────────────────────────────
// Lock model: a rep can always edit notes. Anything touching the commission
// math or identity fields requires either the original 24h self-service
// window (from created_at) or an admin-granted unlock (edit_unlocked_until).
// Admin always has full access, and is the only one who can grant an unlock.
// Backend enforces the same rule independently — this is UX, not the real
// gate.

function isLocked(sale, isAdmin) {
  if (isAdmin) return false;
  const hoursOld = (Date.now() - new Date(sale.created_at).getTime()) / 3600000;
  if (hoursOld <= 24) return false;
  if (sale.edit_unlocked_until && new Date(sale.edit_unlocked_until) > new Date()) return false;
  return true;
}

function unlockTimeRemaining(sale) {
  if (!sale.edit_unlocked_until) return null;
  const msLeft = new Date(sale.edit_unlocked_until).getTime() - Date.now();
  if (msLeft <= 0) return null;
  const mins = Math.round(msLeft / 60000);
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function EditSaleModal({ sale, isAdmin, onClose, onSaved }) {
  const { put, post } = useApi();
  // Local override so the unlock button's effect is visible immediately,
  // without depending on the parent's list refetch completing first (that's
  // async and won't have landed yet at the moment this re-renders).
  const [unlockOverride, setUnlockOverride] = useState(null);
  const effectiveSale = unlockOverride ? { ...sale, edit_unlocked_until: unlockOverride } : sale;
  const locked = isLocked(effectiveSale, isAdmin);
  const remaining = unlockTimeRemaining(effectiveSale);

  const [form, setForm] = useState({
    memberEmail: sale.member_email || '', memberFirstName: sale.member_first_name || '',
    memberLastName: sale.member_last_name || '', saleType: sale.sale_type || '',
    membershipPlan: sale.membership_plan || '', salePrice: sale.sale_price ?? '',
    oldPrice: sale.old_price ?? '', billingCycle: sale.billing_cycle || 'monthly',
    notes: sale.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState(null);

  function update(f, v) { setForm(x => ({ ...x, [f]: v })); }
  const isUpgrade = form.saleType.startsWith('upgrade');

  async function handleUnlock() {
    setUnlocking(true);
    try {
      const updated = await post(`/commission/sales/${sale.sale_id}/unlock`, { hours: 2 });
      setUnlockOverride(updated.edit_unlocked_until);
      onSaved(); // background refresh of the list, doesn't affect this modal directly
    } catch (e) {
      setError(e.message || 'Failed to unlock.');
    } finally {
      setUnlocking(false);
    }
  }

  async function handleSave() {
    setError(null);
    setSaving(true);
    try {
      await put(`/commission/sales/${sale.sale_id}`, {
        ...form,
        salePrice: form.salePrice !== '' ? Number(form.salePrice) : undefined,
        oldPrice: isUpgrade && form.oldPrice !== '' ? Number(form.oldPrice) : undefined,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  }

  // Notes are always editable regardless of lock — everything else is
  // disabled when locked. Admin bypasses the lock entirely.
  const fieldsDisabled = locked;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="modal-title">Edit Commission Sale</div>

        {error && (
          <div style={{ padding: '0.6rem 0.75rem', background: 'rgba(255,50,50,0.1)', border: '1px solid var(--red)', borderRadius: 6, fontSize: '0.83rem', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {locked && (
          <div style={{ padding: '0.6rem 0.75rem', background: 'rgba(234,179,8,0.1)', border: '1px solid var(--yellow)', borderRadius: 6, fontSize: '0.8rem', marginBottom: '1rem' }}>
            🔒 This sale is more than 24 hours old — only Notes can be edited. Ask an Admin to unlock it if something else needs fixing.
          </div>
        )}
        {!locked && !isAdmin && remaining && (
          <div style={{ padding: '0.6rem 0.75rem', background: 'rgba(34,197,94,0.1)', border: '1px solid var(--green)', borderRadius: 6, fontSize: '0.8rem', marginBottom: '1rem' }}>
            🔓 Unlocked for editing — {remaining} remaining, then it locks again automatically.
          </div>
        )}
        {isAdmin && (
          <div style={{ padding: '0.6rem 0.75rem', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.8rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span>
              {isLocked(effectiveSale, false)
                ? '🔒 Locked for the rep (past 24h, no active unlock).'
                : remaining ? `🔓 Unlocked for the rep — ${remaining} remaining.` : '🟢 Within the 24h self-service window.'}
            </span>
            <button className="btn btn-ghost btn-sm" disabled={unlocking} onClick={handleUnlock}>
              {unlocking ? 'Unlocking...' : '🔓 Unlock for 2h'}
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div className="field-group" style={{ gridColumn: 'span 2' }}>
            <label className="field-label">Member Email</label>
            <input className="field-input" value={form.memberEmail} disabled={fieldsDisabled}
              onChange={e => update('memberEmail', e.target.value)} />
          </div>
          <div className="field-group">
            <label className="field-label">First Name</label>
            <input className="field-input" value={form.memberFirstName} disabled={fieldsDisabled}
              onChange={e => update('memberFirstName', e.target.value)} />
          </div>
          <div className="field-group">
            <label className="field-label">Last Name</label>
            <input className="field-input" value={form.memberLastName} disabled={fieldsDisabled}
              onChange={e => update('memberLastName', e.target.value)} />
          </div>
          <div className="field-group" style={{ gridColumn: 'span 2' }}>
            <label className="field-label">Sale Type</label>
            <select className="field-select" value={form.saleType} disabled={fieldsDisabled}
              onChange={e => update('saleType', e.target.value)}>
              {SALE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label} ({t.rate})</option>)}
            </select>
          </div>
          <div className="field-group" style={{ gridColumn: 'span 2' }}>
            <label className="field-label">Membership Plan</label>
            <input className="field-input" value={form.membershipPlan} disabled={fieldsDisabled}
              onChange={e => update('membershipPlan', e.target.value)} />
          </div>
          {isUpgrade && (
            <div className="field-group" style={{ gridColumn: 'span 2' }}>
              <label className="field-label">Old Price (prior plan)</label>
              <input type="number" step="0.01" className="field-input" value={form.oldPrice} disabled={fieldsDisabled}
                onChange={e => update('oldPrice', e.target.value)} />
            </div>
          )}
          <div className="field-group">
            <label className="field-label">Price</label>
            <input type="number" step="0.01" className="field-input" value={form.salePrice} disabled={fieldsDisabled}
              onChange={e => update('salePrice', e.target.value)} />
          </div>
          <div className="field-group">
            <label className="field-label">Billing Cycle</label>
            <select className="field-select" value={form.billingCycle} disabled={fieldsDisabled}
              onChange={e => update('billingCycle', e.target.value)}>
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div className="field-group" style={{ gridColumn: 'span 2', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
            Commission recalculates automatically from these values on save — current: {fmtMoney(sale.commission_amount)}
          </div>
        </div>

        <div className="field-group" style={{ marginBottom: '0.75rem' }}>
          <label className="field-label">Notes {locked && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(always editable)</span>}</label>
          <textarea className="field-textarea" rows={2} value={form.notes} onChange={e => update('notes', e.target.value)} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sales table ────────────────────────────────────────────────────────────

// Contact history for one sale (BK, 10/9/26): opened by clicking the member's name, so the notes,
// logged outcomes and Salesmsg threads behind a sale can be checked without leaving this page.
function SaleTimelineModal({ sale, onClose }) {
  const e = sale.effort || {};
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={ev => ev.stopPropagation()} style={{ maxWidth: 720, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
          <div>
            <div className="modal-title" style={{ marginBottom: 4 }}>{sale.member_first_name} {sale.member_last_name}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Sold {String(sale.sale_date).slice(0, 10)} · {sale.location_id} · {sale.membership_plan} · {fmtMoney(sale.sale_price)} · commission {fmtMoney(sale.commission_amount)}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 2 }}>
              Before the sale: {e.contacted || 0} contacted / {e.reached || 0} reached
              {e.salesmsg ? ` · Salesmsg ${e.salesmsg.textsOut} texts, ${e.salesmsg.callsOut} calls, ${e.salesmsg.replies} replies` : ''}
            </div>
            <div style={{ marginTop: 6 }}>
              <ContactLines email={sale.member_email} name={`${sale.member_first_name} ${sale.member_last_name}`} clubId={sale.location_id} />
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
        <div style={{ marginTop: '1rem' }}>
          <MemberTimeline
            memberId={sale.member_id || `sale-${sale.sale_id}`}
            saleId={sale.sale_id}
            memberName={`${sale.member_first_name} ${sale.member_last_name}`}
            maxHeight={520}
          />
        </div>
      </div>
    </div>
  );
}

function SalesTable({ sales, onEditSale, onOpenSale }) {
  return (
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Date</th>
            <th style={styles.th}>Member</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Club</th>
            <th style={styles.th}>Plan</th>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Billing</th>
            <th style={styles.th}>Price</th>
            <th style={styles.th}>Rack / Gap</th>
            <th style={styles.th}>Commission</th>
            <th style={styles.th} title="The rep's outreach to this person up to the sale date: logged outcomes plus Salesmsg calls and texts, counted once per day. Reached = a two-way conversation (a logged conversation outcome, a text reply, or an answered call).">Contacted / Reached</th>
            <th style={styles.th}>Retention</th>
            <th style={styles.th}>Notes</th>
            <th style={styles.th}></th>
          </tr>
        </thead>
        <tbody>
          {sales.length === 0 && (
            <tr><td colSpan={14} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No sales logged yet</td></tr>
          )}
          {sales.map(s => (
            <tr key={s.sale_id} className="table-row-hover">
              <td style={styles.td}>{String(s.sale_date).slice(0, 10)}</td>
              <td style={styles.td}>
                <button type="button" onClick={() => onOpenSale(s)} title="Open contact history"
                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent, #eab308)', cursor: 'pointer', font: 'inherit', textAlign: 'left', textDecoration: 'underline dotted' }}>
                  {s.member_first_name} {s.member_last_name}
                </button>
              </td>
              <td style={{ ...styles.td, color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{s.member_email || '—'}</td>
              <td style={styles.td}><span className="badge badge-gray">{s.location_id}</span></td>
              <td style={styles.td}>{s.membership_plan}</td>
              <td style={styles.td}>
                {typeBadge(s.sale_type)}
                {s.winback_sub_type && (
                  <span className="badge badge-gray" style={{ marginLeft: 4, fontSize: '0.65rem' }}>
                    {s.winback_sub_type === 'save_attempted' ? 'Save Attempted' : 'Uncontacted'}
                  </span>
                )}
                {s.status === 'duplicate_flagged' && <span className="badge badge-red" style={{ marginLeft: 4 }}>Dupe</span>}
                {s.status === 'voided' && <span className="badge badge-gray" style={{ marginLeft: 4 }}>Voided</span>}
              </td>
              <td style={styles.td}>{billingTag(s.sale_type)}</td>
              <td style={styles.td}>{fmtMoney(s.sale_price)}</td>
              <td style={{ ...styles.td, color: s.rack_price && Number(s.rack_price) > Number(s.sale_price) ? 'var(--yellow)' : 'var(--text-secondary)' }}>
                {s.rack_price ? `${fmtMoney(s.rack_price)} (${Number(s.rack_price) > Number(s.sale_price) ? '−' : '+'}${fmtMoney(Math.abs(s.rack_price - s.sale_price))})` : '—'}
              </td>
              <td style={{ ...styles.td, fontWeight: 700, color: 'var(--green)' }}>{fmtMoney(s.commission_amount)}</td>
              <td style={styles.td} title={s.effort?.firstContact ? `First ${s.effort.firstContact}, last ${s.effort.lastContact}` : 'No logged contact in COMS'}>
                {s.effort?.contacted
                  ? <><span style={{ fontWeight: 600 }}>{s.effort.contacted}</span> / <span style={{ fontWeight: 600, color: s.effort.reached ? 'var(--green)' : 'var(--text-secondary)' }}>{s.effort.reached}</span></>
                  : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                {s.effort?.salesmsg && (
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
                       title="Salesmsg activity by the rep before the sale. Counted by day, only where no logged outcome covers that day.">
                    💬 {s.effort.salesmsg.textsOut} text{s.effort.salesmsg.textsOut === 1 ? '' : 's'}
                    {s.effort.salesmsg.callsOut ? ` · ${s.effort.salesmsg.callsOut} call${s.effort.salesmsg.callsOut === 1 ? '' : 's'}` : ''}
                    {s.effort.salesmsg.replies ? ` · ${s.effort.salesmsg.replies} repl${s.effort.salesmsg.replies === 1 ? 'y' : 'ies'}` : ''}
                  </div>
                )}
              </td>
              <td style={styles.td}><RetentionDots retention={s.retention} /></td>
              <td style={{ ...styles.td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.notes || '—'}</td>
              <td style={styles.td}>
                <button className="btn btn-ghost btn-sm" onClick={() => onEditSale(s)}>Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

export default function CommissionModule() {
  const { get } = useApi();
  const { isAdmin } = useAuth();
  const [overview, setOverview] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogSale, setShowLogSale] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [viewingSale, setViewingSale] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  // Download CSV (9/9/26) — backend returns the CSV text embedded in a normal
  // JSON response (same proven auth/parsing path as every other call here),
  // and the actual file download happens entirely client-side via a Blob and
  // a temporary anchor click — standard pattern, no server-side binary
  // response involved.
  async function handleExport() {
    setExporting(true);
    try {
      const result = await get(`/commission/export?month=${month}`);
      const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || 'Export failed — check that this month has logged sales.');
    } finally {
      setExporting(false);
    }
  }

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      get(`/commission/overview?month=${month}`),
      get(`/commission/sales?month=${month}`),
    ])
      .then(([ov, sl]) => { setOverview(ov); setSales(sl.sales || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  return (
    <div className="module-page">
      {showLogSale && (
        <LogSaleModal onClose={() => setShowLogSale(false)} onSaved={() => { setShowLogSale(false); load(); }} />
      )}
      {viewingSale && <SaleTimelineModal sale={viewingSale} onClose={() => setViewingSale(null)} />}
      {editingSale && (
        <EditSaleModal sale={editingSale} isAdmin={isAdmin}
          onClose={() => setEditingSale(null)}
          onSaved={load} />
      )}

      <div className="module-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 22 }}>💵</span>
          <h1 className="module-title">My Commission</h1>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input type="month" className="field-input" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 150 }} />
          <button className="btn btn-ghost btn-sm" disabled={exporting} onClick={handleExport}>
            {exporting ? 'Preparing...' : '⬇ Download CSV'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowLogSale(true)}>+ Log Sale</button>
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><div className="spinner" /></div>
      ) : (
        <>
          <WelldReadyPanel isAdmin={isAdmin} />
          <KPIBar overview={overview} />
          <SalesTable sales={sales} onEditSale={setEditingSale} onOpenSale={setViewingSale} />
        </>
      )}
    </div>
  );
}

const styles = {
  kpiBar: {
    display: 'flex', gap: 0,
    background: '#141414', border: '1px solid var(--border)',
    borderRadius: 8, overflow: 'hidden', marginBottom: '1rem', flexWrap: 'wrap',
  },
  kpiItem: {
    flex: 1, padding: '0.6rem 0.75rem',
    borderRight: '1px solid var(--border)',
    minWidth: 100, textAlign: 'center',
  },
  kpiValue: { fontSize: '1.15rem', fontWeight: 700, color: 'var(--text)', lineHeight: 1.2, fontFamily: 'DM Mono, monospace' },
  kpiLabel: { fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: '0.15rem', whiteSpace: 'nowrap' },
  tableWrapper: {
    overflowX: 'auto',
    background: '#141414', border: '1px solid var(--border)',
    borderRadius: 8, position: 'relative',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem', minWidth: 800 },
  th: {
    padding: '0.55rem 0.85rem', textAlign: 'left',
    fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: '1px solid var(--border)', background: '#141414', whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.55rem 0.85rem', borderBottom: '1px solid var(--border)',
    color: 'var(--text)', whiteSpace: 'nowrap',
  },
};
